import React from 'react';
import { Button, Icons, Label, Variants, TextInput } from '@sede-enterprise/shell-ds-react-framework';
import styled from 'styled-components';
import { Row, Col } from '../flexbox/Flex';

const TransferContainer = styled.div`
    display: flex;
    gap: 10px;
    margin: 0;
    align-items: flex-start;
`;

const ListBox = styled.div`
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    height: 200px;
    overflow-y: auto;
    background-color: #fafafa;
    padding: 5px;
    flex: 1;
`;

const ListItem = styled.div<{ selected?: boolean }>`
    padding: 6px 8px;
    margin: 4px 0;
    background-color: ${props => props.selected ? '#e6f7ff' : 'white'};
    border: 1px solid ${props => props.selected ? '#0097BB' : '#d9d9d9'};
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 13px;

    &:hover {
        border-color: #0097BB;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    justify-content: center;
    align-items: center;
    height: 200px;
    margin-top: 44px;
    
    button {
        padding: 6px 8px;
        min-width: 32px;
        height: 32px;
        
        svg {
            width: 16px;
            height: 16px;
        }
    }
`;

interface TransferListProps {
    availableItems: string[];
    selectedItems: string[];
    onItemsChange: (selected: string[]) => void;
    leftTitle: string;
    rightTitle: string;
}

const TransferList: React.FC<TransferListProps> = ({
    availableItems,
    selectedItems,
    onItemsChange,
    leftTitle,
    rightTitle
}) => {
    const [leftSelected, setLeftSelected] = React.useState<string[]>([]);
    const [rightSelected, setRightSelected] = React.useState<string[]>([]);
    const [leftSearch, setLeftSearch] = React.useState<string>('');
    const [rightSearch, setRightSearch] = React.useState<string>('');
    const [draggedItem, setDraggedItem] = React.useState<string | null>(null);

    const unselectedItems = availableItems.filter(item => !selectedItems.includes(item));

    // Filter items based on search
    const filteredUnselectedItems = unselectedItems.filter(item => 
        item.toLowerCase().includes(leftSearch.toLowerCase())
    );

    const filteredSelectedItems = selectedItems.filter(item => 
        item.toLowerCase().includes(rightSearch.toLowerCase())
    );

    const handleLeftClick = (item: string) => {
        if (leftSelected.includes(item)) {
            setLeftSelected(leftSelected.filter(i => i !== item));
        } else {
            setLeftSelected([...leftSelected, item]);
        }
    };

    const handleRightClick = (item: string) => {
        if (rightSelected.includes(item)) {
            setRightSelected(rightSelected.filter(i => i !== item));
        } else {
            setRightSelected([...rightSelected, item]);
        }
    };

    const moveToRight = () => {
        const newSelected = [...selectedItems, ...leftSelected];
        onItemsChange(newSelected);
        setLeftSelected([]);
    };

    const moveToLeft = () => {
        const newSelected = selectedItems.filter(item => !rightSelected.includes(item));
        onItemsChange(newSelected);
        setRightSelected([]);
    };

    const handleDragStart = (item: string) => {
        setDraggedItem(item);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    const handleDrop = (targetItem: string) => {
        if (!draggedItem || draggedItem === targetItem) {
            setDraggedItem(null);
            return;
        }

        const currentIndex = selectedItems.indexOf(draggedItem);
        const targetIndex = selectedItems.indexOf(targetItem);

        if (currentIndex === -1 || targetIndex === -1) {
            setDraggedItem(null);
            return;
        }

        const updated = [...selectedItems];
        updated.splice(currentIndex, 1);
        updated.splice(targetIndex, 0, draggedItem);
        onItemsChange(updated);
        setDraggedItem(null);
    };

    return (
        <div>
            <TransferContainer>
                <div style={{ flex: 1 }}>
                    <Label bold>{leftTitle}</Label>
                    <div style={{ marginBottom: '8px', marginTop: '8px' }}>
                        <TextInput
                            placeholder="Search..."
                            value={leftSearch}
                            onChange={(e) => setLeftSearch(e.target.value)}
                            size="small"
                        />
                    </div>
                    <ListBox>
                        {filteredUnselectedItems.map((item) => (
                            <ListItem
                                key={item}
                                selected={leftSelected.includes(item)}
                                onClick={() => handleLeftClick(item)}
                            >
                                {item}
                            </ListItem>
                        ))}
                        {filteredUnselectedItems.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                {leftSearch ? 'No matching items' : 'No items available'}
                            </div>
                        )}
                    </ListBox>
                </div>

                <ButtonContainer>
                    <Button
                        variant={Variants.Outlined}
                        onClick={moveToRight}
                        disabled={leftSelected.length === 0}
                        icon={<Icons.ChevronRight />}
                    />
                    <Button
                        variant={Variants.Outlined}
                        onClick={moveToLeft}
                        disabled={rightSelected.length === 0}
                        icon={<Icons.ChevronLeft />}
                    />
                </ButtonContainer>

                <div style={{ flex: 1 }}>
                    <Label bold>{rightTitle}</Label>
                    <div style={{ marginBottom: '8px', marginTop: '8px' }}>
                        <TextInput
                            placeholder="Search..."
                            value={rightSearch}
                            onChange={(e) => setRightSearch(e.target.value)}
                            size="small"
                        />
                    </div>
                    <ListBox>
                        {filteredSelectedItems.map((item) => (
                            <ListItem
                                key={item}
                                selected={rightSelected.includes(item)}
                                onClick={() => handleRightClick(item)}
                                draggable
                                onDragStart={() => handleDragStart(item)}
                                onDragOver={handleDragOver}
                                onDrop={() => handleDrop(item)}
                            >
                                {item}
                            </ListItem>
                        ))}
                        {filteredSelectedItems.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                {rightSearch ? 'No matching items' : 'No items selected'}
                            </div>
                        )}
                    </ListBox>
                </div>
            </TransferContainer>
        </div>
    );
};

export default TransferList;